import React, { useCallback, useRef } from 'react';
import { Upload, FileSpreadsheet } from 'lucide-react';

interface FileUploadZoneProps {
  onFileSelect: (file: File) => void;
  selectedFile: File | null;
  acceptedTypes: string;
}

const FileUploadZone: React.FC<FileUploadZoneProps> = ({
  onFileSelect,
  selectedFile,
  acceptedTypes,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();

      const files = e.dataTransfer.files;
      if (files.length > 0) {
        onFileSelect(files[0]);
      }
    },
    [onFileSelect]
  );

  const handleFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files;
      if (files && files.length > 0) {
        onFileSelect(files[0]);
      }
    },
    [onFileSelect]
  );

  const handleClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <div data-test-id="file-upload-zone">
      <div
        className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-all
          ${
            selectedFile
              ? 'border-blue-500 bg-blue-50'
              : 'border-gray-300 hover:border-gray-400 bg-gray-50 hover:bg-gray-100'
          }`}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        onClick={handleClick}
        data-test-id="file-upload-drop-zone"
      >
        <input
          ref={fileInputRef}
          type="file"
          accept={acceptedTypes}
          onChange={handleFileChange}
          className="hidden"
          data-test-id="file-upload-input"
        />

        {!selectedFile ? (
          <>
            <Upload className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3
              className="text-lg font-medium text-gray-900 mb-2"
              data-test-id="file-upload-title"
            >
              Upload your loan file
            </h3>
            <p
              className="text-sm text-gray-600 mb-4"
              data-test-id="file-upload-description"
            >
              Drag and drop your Excel or CSV file here, or click to browse
            </p>
            <p
              className="text-xs text-gray-500"
              data-test-id="file-upload-formats"
            >
              Supported formats: .xlsx, .xls, .csv (Max 10MB)
            </p>
          </>
        ) : (
          <>
            <FileSpreadsheet className="w-12 h-12 text-blue-600 mx-auto mb-4" />
            <h3
              className="text-lg font-medium text-gray-900 mb-2"
              data-test-id="file-upload-selected-title"
            >
              File selected
            </h3>
            <div className="space-y-1" data-test-id="file-upload-selected-info">
              <p
                className="text-sm font-medium text-gray-700"
                data-test-id="file-upload-filename"
              >
                {selectedFile.name}
              </p>
              <p
                className="text-sm text-gray-600"
                data-test-id="file-upload-filesize"
              >
                {formatFileSize(selectedFile.size)}
              </p>
            </div>
            <p
              className="text-xs text-gray-500 mt-4"
              data-test-id="file-upload-change-hint"
            >
              Click to select a different file
            </p>
          </>
        )}
      </div>

      {selectedFile && (
        <div
          className="mt-4 p-4 bg-blue-50 rounded-lg"
          data-test-id="file-upload-info-box"
        >
          <h4
            className="text-sm font-medium text-blue-900 mb-2"
            data-test-id="file-upload-info-title"
          >
            Before importing:
          </h4>
          <ul
            className="text-sm text-blue-800 space-y-1"
            data-test-id="file-upload-info-list"
          >
            <li>• Ensure your file contains the required loan fields</li>
            <li>• The first row should contain column headers</li>
            <li>• Date fields should be in YYYY-MM-DD format</li>
            <li>• Numeric fields should not contain currency symbols</li>
          </ul>
        </div>
      )}
    </div>
  );
};

export default FileUploadZone;
